// src/pages/CallbackPage.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    AUTH_STATE_KEY,
    CODE_VERIFIER_KEY,
    clearAuthData,
    exchangeCodeForToken,
    storeTokenData,
} from '../utils/redditAuth';

const CallbackPage: React.FC = () => {
    const [message, setMessage] = useState<string>('Processing authentication...');
    const [error, setError] = useState<string | null>(null);
    const location = useLocation(); // Hook to access URL information
    const navigate = useNavigate(); // Hook to navigate programmatically

    useEffect(() => {
        const processCallback = async () => {
            try {
                // 1. Parse the URL *query parameters* (?...)
                const params = new URLSearchParams(location.search);
                const code = params.get('code');
                const receivedState = params.get('state');
                const errorParam = params.get('error');

                // 2. Retrieve stored state and code_verifier from localStorage
                const storedState = localStorage.getItem(AUTH_STATE_KEY);
                const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY);

                // 3. Clean up stored state and verifier immediately
                localStorage.removeItem(AUTH_STATE_KEY);
                localStorage.removeItem(CODE_VERIFIER_KEY);

                // 4. Validate State and check for errors
                if (errorParam) {
                    throw new Error(`Reddit authentication failed: ${errorParam}`);
                }
                if (!receivedState || receivedState !== storedState) {
                    throw new Error('State mismatch. Authentication failed for security reasons.');
                }
                if (!code) {
                    throw new Error('Authorization code missing in Reddit response.');
                }
                if (!codeVerifier) {
                    // This should not happen if login flow was correct
                    throw new Error('Code verifier missing from storage. Cannot complete authentication.');
                }

                // 5. Exchange code for tokens
                setMessage('Exchanging code for token...');
                const tokenData = await exchangeCodeForToken(code, codeVerifier);

                // 6. Store Token Data (Access Token, Refresh Token, Expiry)
                storeTokenData(tokenData.access_token, tokenData.expires_in, tokenData.refresh_token);

                // 7. Success and Redirect
                setMessage('Authentication successful! Redirecting...');
                setTimeout(() => {
                    navigate('/'); // Redirect to Home page after success
                }, 1500);

            } catch (err: any) {
                console.error("Authentication Error:", err);
                setError(err.message || 'An unknown error occurred during authentication.');
                setMessage(''); // Clear processing message
                // Clear any potentially stored auth data on error
                clearAuthData();
                // Ensure state/verifier are removed even if error happened before cleanup
                localStorage.removeItem(AUTH_STATE_KEY);
                localStorage.removeItem(CODE_VERIFIER_KEY);
            }
        };

        processCallback();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    return (
        <div className="content-wrapper">
            <div className="p-6 max-w-md mx-auto mt-10 bg-white dark:bg-gray-800/50 rounded-xl shadow-md text-center space-y-4 border border-gray-200 dark:border-gray-700/50">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Authenticating with Reddit</h1>
                {message && <p className="text-gray-600 dark:text-gray-400">{message}</p>}
                {error && (
                    <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 px-4 py-3 rounded relative text-sm" role="alert">
                        <strong className="font-bold">Error:</strong>
                        <span className="block sm:inline"> {error}</span>
                        <p className="mt-2">Please try <a href="/settings" className="font-semibold hover:underline">logging in</a> again.</p>
                    </div>
                )}
                {/* Loading Spinner */}
                {!error && message.includes('Processing') || message.includes('Exchanging') && (
                    <div className="flex justify-center items-center pt-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CallbackPage;